// The shapes the UI works with. Row types generated from the schema are in database.types.ts;
// several of the types here are hand-written copies of those and can drift (nullability especially).
// Runtime import one way only: stopTiming imports from here with `import type`, so this pairing
// erases to a single edge and creates no cycle.
import { resolveRouteTiming } from "@/lib/routeTiming"
import {
  type DepartureProjection,
  type TimingSource,
  inferPassedStops,
  rejectDisplacedTrackpodClaims,
  resolveStopTiming,
} from "@/lib/stopTiming"
import type { TurnaroundCollection } from "@/lib/turnaroundCollections"
import type { VolumeSource } from "@/lib/volumeReporting"

import {
  ROUTE_KINDS,
  type RouteKind,
  toRouteKind,
} from "../../supabase/functions/_shared/routeKind.ts"

// Database enum types

export type RouteInstanceStatus =
  "scheduled" | "in_progress" | "completed" | "delayed" | "ahead" | "cancelled"

// Legacy alias for backwards compatibility
export type RouteStatus = RouteInstanceStatus | "not_started"

export type StopInstanceStatus =
  "scheduled" | "en_route" | "arrived" | "processing" | "completed" | "skipped"

// Legacy alias
export type StopStatus = StopInstanceStatus

export type StopType = "collection" | "delivery" | "depot" | "break" | "fuel"

export type TurnaroundStatus =
  | "not_started"
  | "en_route"
  | "final_approach"
  | "arrived"
  | "processing"
  | "completed"
  | "departed"

export interface Stop {
  id: string
  routeId: string
  sequence: number
  type: StopType
  name: string
  address: string
  postcode: string
  lat: number
  lng: number
  plannedArrival: Date
  plannedDeparture: Date
  actualArrival?: Date
  actualDeparture?: Date
  estimatedArrival?: Date
  /**
   * A genuine departure ETA, or absent. Never a plan wearing an ETA's clothes: `toStopLegacy`
   * fills this only from the `eta` branch of `departureProjection`, so a surface that shows it
   * as live is right to.
   */
  estimatedDeparture?: Date
  /**
   * The full answer to "when does this stop end", including the two cases a timestamp cannot
   * express -- on site with no departure yet, and nothing known but the plan. Prefer this over
   * `estimatedDeparture` anywhere the distinction is visible to an operator.
   */
  departureProjection?: DepartureProjection
  status: StopStatus
  serviceTimeMinutes: number
  volumeEstimated: number
  volumeActual?: number
  weightEstimated?: number
  weightActual?: number
  loadingUnitsEstimated?: number
  loadingUnitsActual?: number
  loadingUnitsType?:
    "Pallets (UK)" | "Pallets (EUR)" | "Cages" | "Loose Load" | "Magnums" | "Sort Bags"
  locationId?: string // Location ID for capacity calculation
  slotType?: string
  /** Optional revenue in GBP when slotType is haulage_delivery (monitoring/planning). */
  haulageDeliveryRevenueGbp?: number | null
  /** Agreed price in GBP when slotType is direct_injection. Prices the whole route it sits on. */
  directInjectionCostGbp?: number | null
  comments: StopComment[]
  delayMinutes?: number
  /**
   * Which system the resolved arrival/departure/status above came from.
   *
   * `actualArrival` and friends are already resolved by the time they reach here — Samsara where
   * it has a value, Track-POD where it does not. These say which, so the UI can mark a
   * driver-entered time as the weaker evidence it is. See src/lib/stopTiming.ts.
   */
  arrivalSource?: TimingSource
  departureSource?: TimingSource
  statusSource?: TimingSource
  /** Samsara and Track-POD tell different stories about this stop. */
  hasSourceDisagreement?: boolean
  /**
   * Does Track-POD hold an order for this stop?
   *
   * `true` means the driver's app had a place to record this stop, so silence about it is a real
   * answer — nothing was recorded — rather than an absence of any opportunity to record.
   * `false` means Track-POD was never given the stop and could not have known, which is what
   * lets its state be deduced from the stops either side. `undefined` means no export has said,
   * and the caller should fall back to what it can tell from the stop's type.
   */
  trackpodIsOrder?: boolean
  /**
   * We sent this stop to Track-POD, looked for its order, and found none.
   *
   * Distinct from "the driver recorded nothing": the order itself is missing or the link to it
   * is broken, which is ours to fix rather than anyone's to chase. Invisible before the export
   * wrote down what it sent — a stop with no driver record looked identical either way.
   */
  trackpodOrderMissing?: boolean
}

export interface StopComment {
  id: string
  stopId: string
  author: string
  content: string
  createdAt: Date
}

export interface Route {
  id: string
  code: string
  /** What kind of day this is. Drives how the route is drawn and whether it is a real run. */
  routeKind: RouteKind
  vehicleReg: string
  driverName: string
  driverId: string
  /**
   * How many drivers this route is run with; 2 is double-manned. Already resolved from the
   * instance and its plan, so a caller never has to know which of the two answered.
   */
  crewSize: number
  /** The mate on a double-manned route. Held only here -- DRB has no field for it. */
  secondDriverId?: string
  secondDriverName?: string
  plannedStartTime: Date
  plannedEndTime: Date
  actualStartTime?: Date
  actualEndTime?: Date
  status: RouteStatus
  /**
   * Which system the resolved route timing and status above came from.
   *
   * Same provenance markers the stops carry, for the same reason: a Track-POD-sourced start is
   * the driver's record where the depot geofence stayed silent, and an operator deciding whether
   * to chase somebody needs to know which of the two they are reading. See src/lib/routeTiming.ts.
   */
  actualStartSource?: TimingSource
  actualEndSource?: TimingSource
  statusSource?: TimingSource
  /** Samsara and Track-POD tell different stories about this route. */
  hasSourceDisagreement?: boolean
  delayMinutes: number
  stops: Stop[]
  totalStops: number
  completedStops: number
  currentLat?: number
  currentLng?: number
  comments: RouteComment[]
  isLocked: boolean
  depot: string
  /** Who runs the route: HIVED, or a partner company whose login is scoped to it. */
  operator?: string
  samsaraRouteId?: string
  estimatedCost?: number
  vehicleCapacity?: number
  distanceMiles?: number // Total route distance in miles (from Samsara)
  durationMinutes?: number // Total route duration in minutes (from Samsara)
  vehicleType?: string // Vehicle type name (e.g., "HGV-Curtainsider-350mi")
  isAdhoc?: boolean // True if route was manually created (not from template)
  // Route instance modification tracking
  plannedRouteId?: string // Reference to parent recurring route (if any)
  isModified?: boolean // True if this instance was manually edited
  modificationNotes?: string
}

export interface RouteComment {
  id: string
  routeId: string
  author: string
  content: string
  createdAt: Date
}

export interface TurnaroundRoute {
  id: string
  vehicleReg: string
  /** Vehicle type name (e.g. "HGV-Curtainsider-350mi"), for telling an HGV from a van. */
  vehicleType?: string
  driverName: string
  routeCode: string
  /** So the board's route code can open the route in Samsara. Absent until the route is exported. */
  samsaraRouteId?: string
  plannedArrival: Date
  estimatedArrival?: Date
  actualArrival?: Date
  arrivalDelayMinutes?: number
  status: TurnaroundStatus
  /** What planning forecast for the whole route. */
  volumeEstimated: number
  /**
   * The sum of the confirmed counts alone, undefined until one exists.
   *
   * Not the figure to put on a board: on a part-counted route it is the total for the counted
   * stops only, so it reads as a route that shrank. Use `volumeReported`.
   */
  volumeActual?: number
  /**
   * The parcel figure the depot should plan around: confirmed counts where the ops team has
   * entered them, forecast for the stops still outstanding. See lib/volumeReporting.ts — the
   * board and the stop sheet must not disagree about how much is on its way.
   */
  volumeReported: number
  volumeSource: VolumeSource
  /** How much of the route has been counted, so the board can say "8 of 12". */
  stopsConfirmed: number
  stopsCarryingVolume: number
  /** Loading units on the same rule, and the unit they are counted in. */
  loadingUnitsReported?: number | null
  loadingUnitType?: string | null
  hasTurnaround: boolean
  nextDeparture?: Date
  nextDestination?: string
  terminatesHere: boolean
  depot: string
  /**
   * What is on the van, shipper by shipper, for this injection.
   *
   * The mobile card's whole content, and the reason it exists: a depot manager reading on a phone
   * wants a line per shipper with a quantity against it, which is what they were typing into Slack
   * by hand. Derived by collectionsForInjection in lib/turnaroundCollections, which is where the
   * window and the actual-beats-forecast rules are stated and tested.
   */
  collections?: TurnaroundCollection[]
  /**
   * The same shippers as names alone, for the desktop column and the CSV.
   *
   * Derived from `collections` rather than gathered separately, so the two cannot come to list
   * different shippers.
   */
  customerNames?: string[]
}

/**
 * Day summary for the stats band.
 *
 * Split deliberately into two groups. The `partition` fields are mutually exclusive and sum to
 * `totalRoutes`; the `exception` fields are subsets of the running/pending routes and overlap
 * with nothing in the partition. The previous shape mixed the two, so the cards on screen never
 * added up and a completed route could also be counted as delayed.
 */
export interface RouteStats {
  totalRoutes: number

  // Partition — mutually exclusive, sums to totalRoutes
  completed: number
  inProgress: number
  notStarted: number
  cancelled: number

  // Exceptions — subsets, counted only among routes still in play
  late: number
  ahead: number
  onTime: number
  overallVehicleCapacity?: {
    totalUsed: number
    totalCapacity: number
    totalRemaining: number
    fillPercentage: number
    basedOnActuals: boolean
    ukPalletEquivalent?: {
      totalCapacity: number // Total UK pallet capacity across all vehicles
      totalUsed: number // Total used capacity in UK pallet equivalents
    }
  }
}


// Planning types

export type CustomerType = "shipper" | "pudo" | "haulage" | "internal" | "other"
export type CustomerStatus = "active" | "inactive" | "pending"
export type LocationStatus = "active" | "inactive" | "pending"
export type LoadingUnitType =
  "Pallets (UK)" | "Pallets (EUR)" | "Cages" | "Loose Load" | "Magnums" | "Sort Bags"
export type SlotType =
  | "shipper_collection"
  | "internal_stop"
  | "haulage_collection"
  | "haulage_delivery"
  | "depot_injection"
  | "direct_injection"
  | "charging_stop"
  /**
   * A spare driver's time, held at a site rather than spent at one.
   *
   * Purely what the row is called. Nothing behavioural hangs off it -- whether a route exports,
   * what it costs and which shift warnings apply are all answered by RouteKind on the route. See
   * supabase/migrations/20260923110000_collections_standby_slot_type.sql.
   */
  | "stand_by"
/**
 * What a route is: the plan holds more than collections.
 *
 * Mirrors the `public.route_kind` enum, and the frontend reads it exactly as the backend does --
 * off the route, never reassembled from its stops. Defined in supabase/functions/_shared/routeKind.ts,
 * which also owns what each value implies.
 *
 *   operational  A route that collects.
 *   standby      A spare driver held against the day. Not exported, costed as hours only.
 *   external     Bought in whole: someone else's vehicle and someone else's driver.
 */
export type { RouteKind }
export { ROUTE_KINDS }

/** What each kind is called on screen. */
export const ROUTE_KIND_LABELS: Record<RouteKind, string> = {
  operational: "Operational",
  standby: "Stand-by",
  external: "External",
}

export type SlotService = "SD" | "ND" | "2D"
export type SlotPnl = "RC1" | "RC2" | "HIVED One" | "HIVED Haulage"
export type DayOfWeek =
  "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday"

export interface Customer {
  id: string
  name: string
  account_id: string // DB column name
  customer_type: CustomerType // DB column name
  status: CustomerStatus
  created_at: string // ISO string from DB
  updated_at: string
  created_by?: string
  updated_by?: string
  // Computed/joined fields
  locations?: Location[]
  location_count?: number
}


// Location-Customer junction table (many-to-many)
export type LocationCustomerRelationshipType = "owner" | "tenant" | "operator" | "partner" | "other"

export interface LocationCustomer {
  id: string
  location_id: string
  customer_id: string
  is_primary: boolean
  relationship_type: LocationCustomerRelationshipType
  notes?: string
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Joined fields
  customer?: Customer
  location?: Location
}

export interface Location {
  id: string
  customer_id?: string // Primary customer (nullable, can use junction table instead)
  site_name: string
  location_id: string // Business-friendly ID
  internal_id?: string
  address: string
  postcode: string
  latitude: number
  longitude: number
  contact_name?: string
  contact_phone?: string
  collection_protocol?: string
  opening_time?: string // HH:mm format (optional)
  closing_time?: string // HH:mm format (optional)
  loading_unit_type?: LoadingUnitType
  parcels_per_loading_unit?: number
  status: LocationStatus
  notes?: string
  // Vehicle limits, added by 20260924130000_collections_location_vehicle_limits.sql.
  allowed_vehicle_types?: string[] | null // collections_vehicle_types.name; null = any
  max_vehicle_height_m?: number | null // null = no known limit
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Computed/joined fields
  customer?: Customer // Primary customer (from customer_id)
  customers?: LocationCustomer[] // All customers (from junction table)
  slots?: Slot[]
  slot_count?: number
}


// Availability slots (Contracted/SLA at locations)

export interface AvailabilitySlot {
  id: string
  location_id: string
  slot_name?: string // Optional friendly name
  start_time: string // HH:mm format - window opens
  end_time: string // HH:mm format - window closes
  service_time_minutes: number
  days: DayOfWeek[] // Which days this slot is available
  slot_type: SlotType
  slot_services: SlotService[]
  slot_pnl: SlotPnl[]
  is_active: boolean
  effective_from?: string // ISO date
  effective_to?: string // ISO date
  notes?: string
  // Provenance, added by 20260902160000_collections_slot_provenance.sql.
  // agreed_arrival is the time the shipper actually agreed; start_time/end_time widen it into the
  // window the vehicle should be on site. confirmed_at is what gates the window check in
  // collections_slot_exceptions -- a slot nobody has checked never raises one.
  agreed_arrival?: string // HH:mm:ss
  source?: "sheet" | "sheet_day_comment" | "plan" | "manual"
  service_time_source?: "plan" | "tranche_default" | "manual"
  confirmed_at?: string | null // ISO timestamp
  confirmed_by?: string | null
  // Planning, added by 20260924120000_collections_slot_planning.sql. Null means unknown or the
  // planner's default throughout; priority is the one that is never null (0 is ordinary).
  injection_depot_id?: string | null // null = any sort hub
  allowed_vehicle_types?: string[] | null // vehicle type names; null = any the site allows
  expected_units?: number | null // in the site's loading_unit_type
  priority?: number
  miss_penalty_gbp?: number | null
  excluded_dates?: string[] | null // yyyy-MM-dd
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Computed/joined fields
  location?: Location
}

// Legacy alias - keep Slot as alias for backwards compatibility
export type Slot = AvailabilitySlot


// Route stops (Operational Planning)

export interface RouteStop {
  id: string
  route_id: string
  location_id: string
  availability_slot_id?: string // Optional reference to contracted slot
  sequence: number
  planned_arrival: string // HH:mm format
  planned_departure: string // HH:mm format
  arrival_day_offset: number // 0 = same day, 1 = next day (for overnight routes)
  departure_day_offset: number // 0 = same day, 1 = next day (for overnight routes)
  service_time_minutes: number
  stop_type: SlotType
  stop_services: SlotService[]
  stop_pnl: SlotPnl[]
  is_start_location: boolean
  is_end_location: boolean
  is_active: boolean
  notes?: string
  /** Optional revenue in GBP when stop_type is haulage_delivery. Shown in cost breakdown. */
  haulage_delivery_revenue_gbp?: number | null
  /** Agreed price in GBP when stop_type is direct_injection. Prices the whole route it sits on. */
  direct_injection_cost_gbp?: number | null
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Computed/joined fields
  location?: Location
  availability_slot?: AvailabilitySlot
}

// Route planned (templates)

export type RoutePlannedStatus = "draft" | "live" | "cancelled"

export type RouteCategory =
  "South Coast" | "PUDO" | "HGV Oakland" | "HGV" | "HIVEDone" | "Other" | "Trunking" | "Haulage"

export const ROUTE_CATEGORIES: RouteCategory[] = [
  "South Coast",
  "PUDO",
  "HGV Oakland",
  "HGV",
  "HIVEDone",
  "Other",
  "Trunking",
  "Haulage",
]

export interface RoutePlanned {
  id: string
  code: string
  name: string
  category?: RouteCategory
  days: DayOfWeek[]
  default_start_time: string // HH:mm
  default_end_time: string // HH:mm
  depot: string
  operator: string
  vehicle_type?: string
  /** Drivers the route is run with; 2 is double-manned. A standing property of the route. */
  crew_size?: number
  /** What kind of day this plans. Standing, like crew_size -- copied onto every instance. */
  route_kind?: RouteKind
  estimated_cost?: number
  status: RoutePlannedStatus
  effective_from: string // ISO date
  effective_to?: string // ISO date
  /** Dates to skip when generating route instances (e.g. ["2026-03-19", "2026-03-20"]) */
  excluded_dates?: string[] | null
  notes?: string
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Computed/joined fields - stops for this route
  stops?: RouteStop[]
  operator_allocations?: OperatorAllocation[]
}

/**
 * A route handed to another operator for an inclusive range of dates. `operator` is null in a
 * partner's payload where the dates belong to some other company.
 */
export interface OperatorAllocation {
  id: string
  operator: string | null
  from_date: string // YYYY-MM-DD
  to_date: string // YYYY-MM-DD
  note?: string | null
}

// Route instances (daily execution)

export interface RouteInstance {
  id: string
  planned_route_id?: string
  code: string
  date: string // ISO date YYYY-MM-DD
  driver_id?: string
  driver_name?: string
  /** The mate on a double-manned route. No DRB counterpart: this tool is the only record. */
  second_driver_id?: string
  second_driver_name?: string
  /** Overrides the plan's crew size for this date; null/absent means inherit. Set on ad-hoc
   *  routes, which have no plan to inherit from. */
  crew_size?: number | null
  /** What kind of day this is. Copied from the plan; read directly, with no inherit precedence. */
  route_kind?: RouteKind
  vehicle_reg?: string
  vehicle_type?: string // Denormalised vehicle type name for this instance
  vehicle_capacity_kg?: number
  planned_start_time: string // ISO datetime
  planned_end_time: string
  actual_start_time?: string
  actual_end_time?: string
  status: RouteInstanceStatus
  delay_minutes: number
  current_lat?: number
  current_lng?: number
  last_gps_update?: string
  samsara_route_id?: string
  samsara_route_state?: string
  // Present on the row and returned by get_route_instances_for_date, which
  // selects *.
  trackpod_route_id?: string
  /**
   * Track-POD's route lifecycle, written only by the Track-POD ingest.
   *
   * Enrichment, exactly like the trackpod_* columns on a stop: actual_start_time and
   * actual_end_time stay Samsara-owned, and the two are combined on read by resolveRouteTiming in
   * src/lib/routeTiming.ts. `trackpod_status` is the raw RouteStatus string — Draft, Ready,
   * Loaded, InProgress or Closed — stored unmapped so a new value costs a resolver change rather
   * than a migration.
   */
  trackpod_status?: string | null
  trackpod_start?: string | null
  trackpod_close?: string | null
  trackpod_synced_at?: string | null
  depot: string
  operator: string
  estimated_cost?: number
  distance_miles?: number // Total route distance in miles (from Samsara)
  duration_minutes?: number // Total route duration in minutes (from Samsara)
  is_adhoc: boolean
  is_locked: boolean
  total_stops: number
  completed_stops: number
  notes?: string
  // Modification tracking - for one-off changes to recurring routes
  is_modified: boolean
  modification_notes?: string
  modified_at?: string
  modified_by?: string
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Joined fields
  stops?: StopInstance[]
  comments?: RouteComment[]
  planned_route?: RoutePlanned
}

export interface StopInstance {
  id: string
  route_instance_id: string
  planned_stop_id?: string
  location_id?: string
  sequence: number
  name: string
  address: string
  postcode: string
  lat: number
  lng: number
  stop_type: SlotType
  slot_services: SlotService[]
  slot_pnl: SlotPnl[]
  planned_arrival: string // ISO datetime
  planned_departure: string
  arrival_day_offset: number // 0 = same day, 1 = next day (for overnight routes)
  departure_day_offset: number // 0 = same day, 1 = next day (for overnight routes)
  service_time_minutes: number
  estimated_arrival?: string
  estimated_departure?: string
  actual_arrival?: string
  actual_departure?: string
  status: StopInstanceStatus
  delay_minutes: number
  samsara_stop_id?: string
  /**
   * Track-POD's record of the same stop, written by collections-trackpod-sync's
   * ingest action. Enrichment only: the columns above stay Samsara-owned, and
   * the two are combined on read by resolveStopTiming in src/lib/stopTiming.ts.
   */
  trackpod_order_id?: string | null
  trackpod_arrival?: string | null
  trackpod_departure?: string | null
  /** Raw Track-POD status ("Collected", "Not Collected", ...), unmapped. */
  trackpod_status?: string | null
  trackpod_synced_at?: string | null
  /**
   * Whether Track-POD holds an order for this stop, written by the export.
   *
   * Three-valued, and the third value carries weight: null means no export has
   * decided — a route never pushed to Track-POD, or a stop last exported before the column
   * existed — which is not the same claim as false. The resolver falls back to judging by stop
   * type there, and the next export replaces the guess with the fact. See
   * partitionStopsForExport in supabase/functions/_shared/trackpodOrders.ts, which is the one
   * definition of what Track-POD is given.
   */
  trackpod_is_order?: boolean | null
  /** The order Number this stop was exported under, so a rename cannot break the match. */
  trackpod_order_number?: string | null
  trackpod_exported_at?: string | null
  volume_forecast: number
  weight_forecast_kg?: number
  loading_units_forecast: number
  loading_unit_type?: LoadingUnitType
  volume_actual?: number
  weight_actual_kg?: number
  loading_units_actual?: number
  is_adhoc: boolean
  is_skipped: boolean
  skip_reason?: string
  notes?: string
  /** Optional revenue in GBP when stop_type is haulage_delivery. Shown in cost breakdown. */
  haulage_delivery_revenue_gbp?: number | null
  /** Agreed price in GBP when stop_type is direct_injection. Prices the whole route it sits on. */
  direct_injection_cost_gbp?: number | null
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  // Joined fields
  location?: Location
  planned_slot?: Slot
  comments?: StopComment[]
}

// Legacy types for backwards compatibility

export interface PlannedStop {
  id: string
  routeId: string
  sequence: number
  type: StopType
  locationId: string
  slotId?: string
  name: string
  address: string
  postcode: string
  lat: number
  lng: number
  plannedArrival: Date
  plannedDeparture: Date
  serviceTimeMinutes: number
  volumeEstimated: number
  loadingUnitsEstimated: number
  notes?: string
  // Extended fields for slot information
  slotType?: SlotType
  slotServices?: SlotService[]
  slotPnl?: SlotPnl[]
  /** Optional revenue in GBP when slotType is haulage_delivery. Shown in cost breakdown. */
  haulageDeliveryRevenueGbp?: number | null
  /** Agreed price in GBP when slotType is direct_injection. Prices the whole route it sits on. */
  directInjectionCostGbp?: number | null
}

export interface PlannedRoute {
  id: string
  code: string
  date: Date
  /**
   * Assignment, present only when this row is standing in for a route instance -- the planning
   * list swaps in instance times and stops for a date that has been generated. A planned route
   * itself has none: it carries a vehicle type, and a driver and a plate are chosen per date in
   * DRB and stored on collections_route_instances.
   */
  vehicleReg?: string
  driverId?: string
  driverName?: string
  /**
   * Drivers the route is run with; 2 is double-manned. A plan property, unlike the assignment
   * fields above -- the plan owns how many people the route takes, not who they are.
   */
  crewSize?: number
  /** The instance this row stands in for, when one has been generated. The
   *  shift cover check answers by instance, so this is how a row finds its
   *  flag; a row with none has nothing to compare and shows none. */
  routeInstanceId?: string
  /** True once the instance has actually rolled. The cover check says nothing
   *  about a started route, and the list should say why rather than go blank. */
  routeInstanceStarted?: boolean
  routeInstanceStatus?: RouteInstanceStatus
  plannedStartTime: Date
  plannedEndTime: Date
  stops: PlannedStop[]
  totalStops: number
  depot: string
  estimatedCost?: number
  isRecurring: boolean
  recurringDays?: DayOfWeek[]
  recurringEndDate?: Date
  /** Dates to skip when generating route instances (shown in plan as excluded) */
  excludedDates?: Date[]
  status: "draft" | "live" | "cancelled"
  /** Carried straight from the `collections_routes_planned` row. */
  category?: RouteCategory
  /** The plan's own operator; on an instance row, that day's. See operatorOn. */
  operator?: string
  operatorAllocations?: OperatorAllocation[]
  vehicleType?: string
  route_kind?: RouteKind
  createdAt: Date
  updatedAt: Date
}


// Vehicle types & config

export interface VehicleType {
  id: string
  name: string
  display_name?: string
  monthly_lease_cost: number
  miles_per_kwh: number
  labour_hourly_rate: number // Hourly labour/driver rate for this vehicle type
  range_miles: number // Vehicle range (miles). Always set. Config % at on-site rate, remainder at off-site. Not used for HGV.
  eur_pallet_capacity?: number
  uk_pallet_capacity?: number
  cage_capacity?: number
  magnum_capacity?: number
  sort_bag_capacity?: number
  // Planning, added by 20260924110000_collections_vehicle_type_planning.sql. Null is unknown.
  driving_regime?: DrivingRegime | null // null = derived from the name, HGV prefix = assimilated
  height_m?: number | null
  gross_weight_kg?: number | null
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
}

/** Drivers' hours rules: assimilated for goods vehicles over 3.5 t, GB domestic up to 3.5 t. */
export type DrivingRegime = "assimilated" | "gb_domestic"

// Depots & fleet

/** Where a load can be injected, with the rules the planner needs. Text depot names on routes stay the display value. */
export interface Depot {
  id: string
  name: string
  location_id: string | null
  aliases: string[]
  unload_bays: number
  bay_slot_minutes: number
  large_load_threshold_units: number | null
  unload_fixed_minutes: number
  unload_minutes_per_unit: number
  /** Latest arrival per service, e.g. {"SD": "17:00"}. A missing key is no cut-off. */
  service_cutoffs: Partial<Record<SlotService, string>>
  opens: string | null
  closes: string | null
  /** Loads are tipped and sorted here, so any collection may be injected. False for a base or spoke. */
  is_sort_hub?: boolean
  is_active: boolean
  notes: string | null
  created_at: string
  updated_at: string
}

export interface CollectionsConfig {
  id: string
  driver_wage_rate_per_hour: number // Fallback when vehicle has no labour_hourly_rate
  /** Total fleet cost per month (GBP). Divided by 30 and added to each day of the weekly plan. */
  fleet_cost_per_month_gbp: number
  /** Max driver working hours per day (e.g. 12). Used for haulage: extended sellable time before first stop. */
  driver_max_hours_per_day?: number
  /** Min empty leg % of total route to count as "meaningful" for haulage applicable (e.g. 10). */
  haulage_min_empty_pct?: number
  /** Min extended sellable minutes (before first stop) to count as haulage applicable (e.g. 60). */
  haulage_min_extended_minutes?: number
  /** On Site Charging Cost (Internal) £/kWh. Global rate for on-site charging. */
  on_site_charging_cost_per_kwh?: number
  /** Off Site Fast Charging (external) £/kWh. Global rate for charging beyond internal range. */
  default_cost_per_kwh: number
  /** Oaklands Charging Cost (HGV) £/kWh. Used for the remainder of HGV mileage (after internal_charging_range_pct at on-site rate). */
  oaklands_cost_per_kwh?: number
  /** Percentage of vehicle range charged at internal (on-site) rate; remainder at external. e.g. 70. */
  internal_charging_range_pct?: number
  planned_mileage_percentage_add: number // e.g. 15 = +15% on planned (crow-fly) mileage to approximate road mileage
  uk_pallet_ratio_sort_bag?: number
  uk_pallet_ratio_cage?: number
  uk_pallet_ratio_magnum?: number
  uk_pallet_ratio_eur_pallet?: number
  uk_pallet_ratio_uk_pallet?: number
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
}


// Calculated route costs. Defined beside the cost model it describes.
export type { RouteCostBreakdown } from "../../supabase/functions/_shared/routeCost.ts"

// Utility functions for type conversion

/**
 * Convert a RouteInstance to the legacy Route the monitoring views read.
 *
 * The stop-level Samsara/Track-POD resolution in toStopLegacy is finished by the time this runs,
 * so the route-level figures are derived from the resolved stops rather than from the columns
 * beside them. `completed_stops` in particular is maintained by a database trigger counting raw
 * `status = 'completed'` rows, which is Samsara's answer — leaving the header saying "0 / 5" over
 * a list showing three ticks, two counts of one thing taken from either side of the merge.
 *
 * Route timing and status come through resolveRouteTiming for the same reason: Track-POD's route
 * lifecycle is the driver's own record of starting and closing the run, and a closed route is the
 * statement of completion a silent depot geofence never made. Note the start time it supplies is
 * a real recorded one — this still never synthesises a start from a stop arrival, which would be
 * an arrival at the first stop rather than a departure from the depot.
 */
export function toRouteLegacy(r: RouteInstance, now?: Date): Route {
  const timing = resolveRouteTiming(r)
  // A route the driver closed in Track-POD is a run that is over, which is what lets the
  // inference reach the return-to-depot leg no order status could ever have covered.
  // Displaced claims are rejected before anything is inferred from what is left. A stop cleared
  // from the wrong place is not evidence about itself, and it is certainly not evidence about its
  // neighbours -- inferring off it would spread one driver's mistap along the run.
  const stops = inferPassedStops(
    rejectDisplacedTrackpodClaims(r.stops?.map((stop) => toStopLegacy(stop, now)) || []),
    { routeClosed: timing.status === "completed" }
  )

  return {
    id: r.id,
    code: r.code,
    routeKind: toRouteKind(r.route_kind),
    vehicleReg: r.vehicle_reg || "",
    driverName: r.driver_name || "",
    driverId: r.driver_id || "",
    plannedStartTime: new Date(r.planned_start_time),
    plannedEndTime: new Date(r.planned_end_time),
    actualStartTime: timing.actualStart,
    actualEndTime: timing.actualEnd,
    status: timing.status === "scheduled" ? "not_started" : (timing.status as RouteStatus),
    actualStartSource: timing.actualStartSource,
    actualEndSource: timing.actualEndSource,
    statusSource: timing.statusSource,
    hasSourceDisagreement: timing.disagreement,
    delayMinutes: r.delay_minutes,
    stops,
    totalStops: r.total_stops,
    completedStops: stops.length > 0
      ? stops.filter((s) => s.status === "completed").length
      : r.completed_stops,
    currentLat: r.current_lat,
    currentLng: r.current_lng,
    comments: r.comments || [],
    // Defensive: some historical rows may have NULL is_locked; treat as locked by default.
    isLocked: r.is_locked ?? true,
    depot: r.depot,
    operator: r.operator,
    samsaraRouteId: r.samsara_route_id,
    estimatedCost: r.estimated_cost,
    vehicleCapacity: r.vehicle_capacity_kg,
    distanceMiles: r.distance_miles,
    durationMinutes: r.duration_minutes,
    vehicleType: r.planned_route?.vehicle_type,
    /**
     * The one place crew size is resolved, so no caller has to know the precedence.
     *
     * The instance wins where it is set, which is how an ad-hoc route says it is double-manned
     * at all: it has no planned_route to inherit from. Otherwise the plan answers, because
     * double-manning is a standing property of a route rather than a fact about a day. One is
     * the floor: a route is never run by nobody.
     */
    crewSize: r.crew_size ?? r.planned_route?.crew_size ?? 1,
    secondDriverId: r.second_driver_id,
    secondDriverName: r.second_driver_name,
    isAdhoc: r.is_adhoc,
    // Modification tracking
    plannedRouteId: r.planned_route_id,
    isModified: r.is_modified,
    modificationNotes: r.modification_notes,
  }
}

/**
 * Which kind of stop each slot type presents as on the timeline.
 *
 * Exported because a stop staged in the UI — added but not yet saved — has to present itself the
 * same way a saved one does, and there should be one answer to what that looks like.
 */
export const slotTypeToStopType: Record<SlotType, StopType> = {
  shipper_collection: "collection",
  haulage_collection: "collection",
  haulage_delivery: "delivery",
  internal_stop: "depot",
  depot_injection: "depot",
  direct_injection: "depot",
  charging_stop: "fuel",
  // The activity, not the address. A spare driver waiting at a depot is not running a depot leg,
  // and anything counting depot stops towards capacity or terminal legs should skip this.
  stand_by: "break",
}

/**
 * Convert a StopInstance to the legacy Stop the UI reads.
 *
 * This is where Samsara and Track-POD are combined. Every monitoring surface — the timeline, the
 * map, the details sheet, the turnarounds board — reads `Stop` rather than `StopInstance`, so
 * resolving here means a geofence miss is corrected everywhere at once, while the underlying
 * columns keep one writer each. See src/lib/stopTiming.ts for the precedence rules.
 */
export function toStopLegacy(s: StopInstance, now?: Date): Stop {
  const resolved = resolveStopTiming(s, now)

  return {
    id: s.id,
    routeId: s.route_instance_id,
    sequence: s.sequence,
    type: slotTypeToStopType[s.stop_type] || "collection",
    name: s.name,
    address: s.address,
    postcode: s.postcode,
    lat: s.lat,
    lng: s.lng,
    plannedArrival: new Date(s.planned_arrival),
    plannedDeparture: new Date(s.planned_departure),
    actualArrival: resolved.arrival,
    actualDeparture: resolved.departure,
    /**
     * Both estimates are dropped the moment an arrival is resolved. An ETA answers "when will the
     * van get here", and once it has, the question is closed -- but Samsara keeps answering it,
     * and a stale reply left in these fields reads on screen exactly like a live one. Suppressing
     * them here rather than at each call site means every surface stops believing them at once.
     */
    estimatedArrival: resolved.arrival
      ? undefined
      : s.estimated_arrival
        ? new Date(s.estimated_arrival)
        : undefined,
    estimatedDeparture:
      resolved.departureProjection.kind === "eta" ? resolved.departureProjection.at : undefined,
    departureProjection: resolved.departureProjection,
    status: resolved.status,
    arrivalSource: resolved.arrivalSource,
    departureSource: resolved.departureSource,
    statusSource: resolved.statusSource,
    hasSourceDisagreement: resolved.disagreement,
    // `?? undefined` rather than `?? false`: a null column is the export not having spoken, and
    // flattening that to "not an order" would hand the resolver a fact it does not have.
    trackpodIsOrder: s.trackpod_is_order ?? undefined,
    // Stamped by the ingest when it looked and Track-POD had nothing: synced, exported as an
    // order, and still no order id against it.
    trackpodOrderMissing:
      s.trackpod_is_order === true && !s.trackpod_order_id && !!s.trackpod_synced_at,
    serviceTimeMinutes: s.service_time_minutes,
    volumeEstimated: s.volume_forecast,
    volumeActual: s.volume_actual,
    weightEstimated: s.weight_forecast_kg,
    weightActual: s.weight_actual_kg,
    loadingUnitsEstimated: s.loading_units_forecast,
    loadingUnitsActual: s.loading_units_actual,
    loadingUnitsType: s.loading_unit_type,
    locationId: s.location_id, // Include location_id for capacity calculation
    slotType: s.stop_type,
    haulageDeliveryRevenueGbp: s.haulage_delivery_revenue_gbp ?? undefined,
    directInjectionCostGbp: s.direct_injection_cost_gbp ?? undefined,
    comments: s.comments || [],
    delayMinutes: s.delay_minutes,
  }
}

// Shift exceptions

/**
 * One disagreement between a planned route instance and the shift booked for it.
 *
 * Mirrors ShiftExceptionRow in supabase/functions/_shared/shiftExceptions.ts,
 * which is the single implementation of the rules. Nothing is derived
 * client-side: the surfaces render what comes out and decide nothing.
 */
export type ShiftExceptionType =
  | "missing_shift"
  | "understaffed_crew"
  | "duplicate_shift"
  | "unfilled_shift"
  | "start_time_mismatch"
  | "end_time_mismatch"
  | "driver_mismatch"
  | "vehicle_mismatch"
  | "vehicle_class_mismatch"
  | "depot_mismatch"
  | "shift_without_route"

/** What the planner has to do about it, which is the only cut the panel needs. */
export type ShiftExceptionSeverity = "unstaffed" | "drift" | "orphan"

export interface ShiftException {
  exceptionType: ShiftExceptionType
  severity: ShiftExceptionSeverity
  routeInstanceId: string | null
  routeCode: string | null
  date: string
  depot: string | null
  driverName: string | null
  plannedStartTime: string | null
  plannedEndTime: string | null
  shiftId: string | null
  shiftStart: string | null
  shiftEnd: string | null
  shiftStatus: string | null
  shiftNotes: string | null
  shiftPersonName: string | null
  /** Optional for the same deploy-skew reason as on CoveredRoute. */
  shiftPersonId?: string | null
  codeTier: string | null
  detail: string
}

/** A route a filled shift matched. Mirrors CoveredRoute on the backend. */
export interface CoveredRoute {
  routeInstanceId: string
  routeCode: string
  date: string
  shiftId: string
  shiftPersonName: string | null
  /** The booked person's business identifier. Optional here and not on the backend:
   *  the edge function deploys separately from this bundle, so a frontend that knows
   *  about it can be talking to a function that does not yet send it. */
  shiftPersonId?: string | null
  shiftStart: string
  shiftEnd: string
  hasDrift: boolean
}

export interface ShiftExceptionsResult {
  from: string
  to: string
  exceptions: ShiftException[]
  /** Routes with a matching filled shift, so "covered" can be said out loud. */
  covered: CoveredRoute[]
  bySeverity: Record<ShiftExceptionSeverity, ShiftException[]>
  /** The number the badge shows. Counted server-side so the badge and the panel
   *  cannot disagree. */
  total: number
  /** The resourcing read hit its page cap, so the set is incomplete. A capped
   *  read otherwise looks exactly like a quiet fortnight. */
  truncated: boolean
}
